/*
  The Data class provides declarative "path" access to a stored object.
  For example, you can set or get values by specifying paths such as: "a.b" or "a.b.c[1]".
*/
import { orderBy } from "../array/orderBy.js";
import { has } from "../object/has.js";
import { get } from "../object/get.js";
import { set } from "../object/set.js";
import { unset } from "../object/unset.js";
import { Type } from "../type/Type.js";
import { objectJoin } from "../object/objectJoin.js";
import { stringRemoveCustomSingleLineComments } from "../string/stringRemoveCustomSingleLineComments.js";
import { arrayAND } from "../array/arrayAND.js";
import { regexStringBuild } from "../regex/regexStringBuild.js";
import { regexBuild } from "../regex/regexBuild.js";
import { DataPath } from "../data-path/DataPath.js";
import { arrayInsert } from "../array/arrayInsert.js";
export class Data {
    constructor(data = {}, settings = {}) {
        this.DataPath = DataPath; // expose DataPath publicly for convenience
        this.data = {};
        this.settings = {};
        this.pureEdges = ['-->', '-{}->', '-[]->'];
        this.pureEdgeRegexString = regexStringBuild(`\\s* (?: --> | -\\[\\]-> | -\\{\\}-> ) \\s*`, 'x');
        this.pureEdgeRegex = regexBuild(this.pureEdgeRegexString, 'xg'); // formerly a regex literal: /\s*(?:-->|-\[\]->|-\{\}->)\s*/g
        this.expressionRegexString = regexStringBuild('\\( .+? \\) (?=\\s*\\])', 'x');
        this.expressionExecRegex = regexBuild('\\[ \\s* ((?&expression)) \\s* \\]', 'xgs', { expression: this.expressionRegexString }); // we include the 'g' flag here since we use it with the regex "exec()" function which will then match and "move forward" in the string (keeps the underlying starting index moving forward)
        this.expressionReplaceRegex = regexBuild('\\[ \\s* ((?&expression)) \\s* \\]', 'xs', { expression: this.expressionRegexString }); // we don't include the 'g' flag here since we use it to iteratively replace within a string and build up an overall condition string (we specifically don't want to replace all here)
        this.statementExecRegexString = regexStringBuild('^ \\s* (?: (?&selectClause)  (?&asClause)  (?&limitClause)?  (?:(?&orderByClause)(?&sortClause)?)?  \\s+ IF \\s+ )? (?&condition) \\s* $', 'x', {
            selectClause: '\\s* SELECT \\s+ (.+?) (?= \\s+AS )',
            asClause: '\\s+ AS \\s+ (.+?) (?= \\s+IF | \\s+LIMIT | \\s+ORDER\\s+BY )',
            limitClause: '\\s+ LIMIT \\s+ (.+?) (?= \\s+IF | \\s+ORDER\\s+BY )',
            orderByClause: '\\s+ ORDER \\s+ BY \\s+ (.+?) (?= \\s+IF | \\s+SORT )',
            sortClause: '\\s+ SORT (.+?) (?= \\s+IF )',
            condition: '(\\{ \\s* \\[ \\s* (?&expression) \\s* \\] (?:\\s*(?&operator)\\s*\\[ \\s* (?&expression) \\s* \\])* \\s* \\})',
            expression: this.expressionRegexString,
            operator: '&& | \\|\\|'
        });
        this.statementExecRegex = regexBuild('^' + this.statementExecRegexString + '$', 'xs');
        this.queryStatementRegexString = regexStringBuild('\\s* (?: (?&selectClause)  (?&asClause)  (?&limitClause)?  (?:(?&orderByClause)(?&sortClause)?)?  \\s+ IF \\s+ )? (?&condition) \\s*', 'x', {
            // these are the same as the corresponding regexes above but without the submatches
            selectClause: '\\s* SELECT \\s+ .+? (?= \\s+AS )',
            asClause: '\\s+ AS \\s+ .+? (?= \\s+IF | \\s+LIMIT | \\s+ORDER\\s+BY )',
            limitClause: '\\s+ LIMIT \\s+ .+? (?= \\s+IF | \\s+ORDER\\s+BY )',
            orderByClause: '\\s+ ORDER \\s+ BY \\s+ .+? (?= \\s+IF | \\s+SORT )',
            sortClause: '\\s+ SORT .+? (?= \\s+IF )',
            condition: '\\{ \\s* \\[ \\s* (?&expression) \\s* \\] (?:\\s*(?&operator)\\s*\\[ \\s* (?&expression) \\s* \\])* \\s* \\} (?= \\s+RETURN | \\s* $ )',
            expression: this.expressionRegexString,
            operator: '&& | \\|\\|'
        });
        this.queryExecRegex = regexBuild('^ (?&body) (?&return)? $', 'xs', {
            "statement": this.queryStatementRegexString,
            "body": '((?&statement) (?:(?&edge)(?&statement))*)',
            "edge": this.pureEdgeRegexString,
            "return": '\\s+ RETURN (.+?) \\s*'
        });
        // set the root data (this throws an error if it's not an object)
        this.set('', data, 'replace');
        objectJoin(this.settings, settings, 'upsert');
        objectJoin(this.settings, {
            "throwPathSyntaxInvalidErrors": false,
            "throwPathDoesNotExistErrors": false
        }, 'append'); // apply these as defaults if they don't already exist
    }
    /*
        Alternative JQL Syntax?

        JSON: {a:0, b:2}
        SEMANTICS: select a+1
        QUERY: SELECT $.a + 1
        RESULT: [ 1 ]

        JSON: {a:0, b:2}
        SEMANTICS: select a+1
        QUERY: SELECT {c: $.a + 1, d: $.b + 1}
        RESULT: [{c:1, d:3}]

        JSON: {a:0, b:2}
        SEMANTICS: select a+1
        QUERY:
            SELECT $.a + 1
            SELECT $.b + 1
        RESULT: [ 1, 3 ]

        JSON: {a:0, b:2}
        SEMANTICS: select a+1 if b has value 2
        QUERY: SELECT $.a + 1 IF $.b === 2
        RESULT: [ 1 ]

        JSON: {a:0, b:2}
        SEMANTICS: select a+1 if any key has value 2 and has name b
        QUERY: SELECT $.a + 1 IF $.* === 2 && $.% === 'b'
        RESULT: [ 1 ]

        JSON: {a:0, b:2}
        SEMANTICS: select a+1 if any key has value 2
        QUERY: SELECT $.a + 1 IF $.* === 2
        RESULT: [ 1 ]

        JSON: {a:0, b:2}
        SEMANTICS: select <value> + 1 if that value equals 2
        QUERY: SELECT $.* + 1 IF $.* === 2
        RESULT: [ 3 ]

        JSON: {a:0, b:2}
        SEMANTICS: select <value> + 1 if any value equals 2
        QUERY: SELECT $.* + 1 IF ANY $.* === 2
        RESULT: [ 1, 3 ] or [ 3, 1 ]

        JSON: { obj1:{a:1, b:0}, obj2: {a:0, b:1}, obj3: {a:2, c:0} }
        SEMANTICS: select a + 1 if it has a sibling named 'b' with value 0
        QUERY: SELECT $.*.a + 1 IF $.*.% === 'b' && $.*.b === 0
        OR:    SELECT $.*.a + 1 IF $.*.b === 0
        RESULT: [ 2 ]

        JSON:
            {
                "HOST-NAME::domain:com": {
                    "Sequencer": {
                        "RequestsOn": {"ODVT-16x16-4-Lanes":"inproc://172.20.240.6:910EED645E43E8006FA150F8CC39439B"},
                        "RespondsOn":{"PrimaryInbox":"inproc://172.20.240.6:53B844E00D991550B3E0B5AC7C667EB2","InterfacesInbox":"ws://172.20.240.6:61732"},
                        "PublishesOn": {"SequenceRunningState":"ws://172.20.240.6:59091"},
                        "lastUpdated":"2019-12-03T23:55:55.970Z",
                        "SubscribesOn":{},
                        "ActorDefinitionName": "Sequencer"
                    }
                },
                "Parsimony-PC::localdomain":{}
            }
        SEMANTICS: select elements at the second level with ActorDefinitionName = Sequencer
        QUERY:


        SELECT {[$.*.%]: $.*.*} IF $.*.*.ActorDefitionName === 'Sequencer'


        RESULT: [
            {
                "Sequencer": {
                    "RequestsOn": {"ODVT-16x16-4-Lanes":"inproc://172.20.240.6:910EED645E43E8006FA150F8CC39439B"},
                    "RespondsOn":{"PrimaryInbox":"inproc://172.20.240.6:53B844E00D991550B3E0B5AC7C667EB2","InterfacesInbox":"ws://172.20.240.6:61732"},
                    "PublishesOn": {"SequenceRunningState":"ws://172.20.240.6:59091"},
                    "lastUpdated":"2019-12-03T23:55:55.970Z",
                    "SubscribesOn":{},
                    "ActorDefinitionName":"Sequencer"
                }
            }
        ]

        JSON:
            {
                "HOST-NAME::domain:com": {
                    "SequencerA": {
                        "RequestsOn": {"ODVT-16x16-4-Lanes":"inproc://172.20.240.6:910EED645E43E8006FA150F8CC39439B"},
                        "RespondsOn":{"PrimaryInbox":"inproc://172.20.240.6:53B844E00D991550B3E0B5AC7C667EB2","InterfacesInbox":"ws://172.20.240.6:61732"},
                        "PublishesOn": {"SequenceRunningState":"ws://172.20.240.6:59091"},
                        "lastUpdated":"2019-12-03T23:55:55.970Z",
                        "SubscribesOn":{},
                        "ActorDefinitionName":"Sequencer"
                    }
                },
                "Parsimony-PC::localdomain":{
                    "SequencerB": {
                        "RequestsOn": {},
                        "RespondsOn":{},
                        "PublishesOn": {},
                        "lastUpdated":"2019-12-03T23:55:55.970Z",
                        "SubscribesOn":{},
                        "ActorDefinitionName":"Sequencer"
                    }
                }
            }
        SEMANTICS: select elements at the second level with ActorDefinitionName = Sequencer
        QUERY: SELECT {[$.*.%]: $.*.*} IF $.*.*.ActorDefitionName === 'Sequencer' RETURN mergeArrayOfObjects($results)
        RESULT: {
                "SequencerA": {
                    "RequestsOn": {"ODVT-16x16-4-Lanes":"inproc://172.20.240.6:910EED645E43E8006FA150F8CC39439B"},
                    "RespondsOn":{"PrimaryInbox":"inproc://172.20.240.6:53B844E00D991550B3E0B5AC7C667EB2","InterfacesInbox":"ws://172.20.240.6:61732"},
                    "PublishesOn": {"SequenceRunningState":"ws://172.20.240.6:59091"},
                    "lastUpdated":"2019-12-03T23:55:55.970Z",
                    "SubscribesOn":{},
                    "ActorDefinitionName":"Sequencer"
                },
                "SequencerB": {
                    "RequestsOn": {},
                    "RespondsOn":{},
                    "PublishesOn": {},
                    "lastUpdated":"2019-12-03T23:55:55.970Z",
                    "SubscribesOn":{},
                    "ActorDefinitionName":"Sequencer"
                }
            }


    NOTE: "AS" forces the return of a named object instead of an array, so that multiple result arrays can be accummulated

        JSON: {a:0, b:2}
        SEMANTICS: select a+1
        QUERY: SELECT $.a + 1 AS "values"
        RESULT: {
                    "values": [ 1 ]
                }

        JSON: {a:0, b:2}
        SEMANTICS: select a+1
        QUERY:
            SELECT $.a + 1 AS "a_values"
            SELECT $.b + 1 AS "b_values"
        RESULT: {
                    "a_values": [ 1 ],
                    "b_values": [ 3 ]
                }

        JSON: {a:0, b:2}
        SEMANTICS: select a+1
        QUERY:
            SELECT $.a + 1 AS "a_values"
            SELECT $.b + 1
            SELECT $.b + 1
        RESULT: {
                    "a_values": [ 1 ],
                    "": [ 3 ]
                }


            // currentLevel: ObjectType, parent: ObjectType,



        SELECT $.*.*.b IF $.*.*.b > 0

        SELECT $.**.b IF $.**.b > 0

        SELECT
        IF      $.*.*.b > 0 && $.*.c > 0



        function($: ObjectType, $keys: string[], $paths: ObjectType)

        SELECT  $data.get(stripPath($paths.nameB, 2))
        IF      nameB:($.**.b) > 0 && nameC:($.**.c) > 0


        function($: ObjectType, $keys: string[], $path: string)

        SELECT  $data.get(stripPath($path, 2))
        IF      $.**.*.d > 0 && $.**.*.c > 0

        SELECT  $data.get(stripPath($path, 2))
        IF      $.**.*.d > 0 && $.*.*.c > 0


     */
    /**
     * Queries the data
     * @param query the query string
     * @param basePath the base path to start the query
     * @returns an object of named results which match the query
     */
    query(query, basePath = "") {
        // JSON Query Language (JQL)
        //
        // JQL Syntax
        // ----------
        // query = <statement> --> <statement> -#-> <statement> -[#]-> ... -{#}-> <statement>
        // statement = SELECT {"thing1": element.a.b, "thing2": element.a.c} AS "<result name>" IF <condition>
        // condition = { [<expression>] && [<expression>] || [<expression>] ... }
        // expression = ( <fragment> && <fragment> || <fragment> ... )
        // fragment = any code fragment that evaluates to type boolean
        //
        // JQL Semantics
        // -------------
        // query = structural semantics
        // statement = result selection semantics
        // condition = object matching semantics
        // expression = element matching semantics
        // fragment = computational semantics
        // if we didn't receive a compiled query, compile it so we can execute it
        if (typeof query === "string") {
            query = this.compileQuery(query);
        }
        // initialize our results
        const results = {};
        // execute the compiled query body (everything but the RETURN clause = STATEMENTS and EDGES) and update the results object by reference
        this.queryExecuteBody(query, this.get(basePath), basePath, results);
        // if we did NOT have a RETURN clause, simply return our results; else excute and return the compiled return clause
        const compiledReturn = query.compiledReturn;
        if (compiledReturn === undefined) {
            return results;
        }
        return compiledReturn(this, results);
    }
    /**
     * Compiles the specified query
     * @param query the query string
     * @returns the compiled query
     */
    compileQuery(query) {
        // - remove # comments
        // - expand the the edges, for example "-2->" becomes "--> (true) -->"
        // - build an array of compilee edges as integers: // 0 = -->, 1 = -{}->, 2 = -[]->
        // - split the query by '-->' into an array of statements and compile those statements
        // query compiles to: {compiledStatements: [<compiled statements>], compiledEdges: [<compiled outgoing edge>]}
        // remove comment lines (lines beginning with '#');
        query = stringRemoveCustomSingleLineComments(query, '#');
        // expand the edges; ex. "-3->" becomes "-->{[(true)]}-->{[(true)]}-->"
        query = this.queryExpandEdges(query);
        // split the query into two pieces: 1. everything but the RETURN clause (i.e STATEMENTS with EDGES), and 2. the RETURN clause if provided.  note: we check for null below but we force type RegExpExecArray below because TS doesn't resolve the fact that we throw an error in our function
        const queryMatches = this.queryExecRegex.exec(query);
        if (queryMatches === null) {
            throw this.newError('InvalidQuery');
        }
        const queryBody = queryMatches[1];
        const queryReturn = queryMatches[2];
        // create the compiled RETURN function if we had a RETURN clause
        const compiledReturnDefinition = `
            // since we allow any type to be selected we do not perform any checks here; we simply return the SELECT clause as-is
            return ${queryReturn};
        `;
        let compiledReturn = undefined;
        if (queryReturn !== undefined) {
            compiledReturn = new Function('$dataManager', '$results', compiledReturnDefinition);
        }
        // build an array of compiled edges
        const compiledEdges = [];
        let edges = queryBody.match(this.pureEdgeRegex);
        if (edges === null) {
            edges = [];
        }
        const N = edges.length;
        for (let i = 0; i < N; i++) {
            compiledEdges[i] = this.pureEdges.indexOf(edges[i].trim()); // 0 = -->, 1 = -{}->, 2 = -[]->
        }
        // build an array of compiled statements
        const compiledStatements = [];
        const statements = queryBody.split(this.pureEdgeRegex);
        const M = statements.length;
        for (let i = 0; i < M; i++) {
            compiledStatements[i] = this.queryCompileStatement(statements[i]);
        }
        return { compiledStatements: compiledStatements, compiledEdges: compiledEdges, compiledReturn: compiledReturn };
    }
    /**
     * Gets the keys at a specified path
     * @param path the data path to get the value for
     * @returns an array of keys at the specified path (empty array for anything but strict object paths)
     */
    keys(path) {
        const value = this.get(path);
        if (Type.isPrimitiveObject(value)) {
            return Object.keys(value);
        }
        return [];
    }
    /**
     * Returns true if specifies an empty path (top level object path), false otherwise
     * @param path the path to check
     * @returns true if specifies an empty path (top level object path), false otherwise
     */
    static isEmptyDataPath(path) {
        return (path === '' || (Array.isArray(path) && path.length === 0));
    }
    /**
     * Returns true if specifies an empty path (top level object path), false otherwise
     * @param path the path to check
     * @returns true if specifies an empty path (top level object path), false otherwise
     */
    isEmptyDataPath(path) {
        return Data.isEmptyDataPath(path);
    }
    /**
     * Checks if a path exists in the underlying data object
     * @param path the data path to check
     * @returns true if the path exists, false otherwise
     */
    has(path) {
        return (this.isEmptyDataPath(path)) ? true : has(this.data, path);
    }
    /**
     * Gets a value at a specified path
     * @param path the data path to get the value for
     * @param defaultValue the default value to return if the path doesn't exist
     * @param clone whether to clone the returned value
     * @returns the value at the specified path
     */
    get(path, defaultValue = undefined, clone = false) {
        // if we have an empty path, return the top level data
        if (path === undefined || this.isEmptyDataPath(path)) {
            return (clone) ? Type.clone(this.data) : this.data;
        }
        // check for path syntax errors if path is a string and we're configured to do so
        if (typeof path === 'string' && this.settings["throwPathSyntaxInvalidErrors"] && !DataPath.isValidPath(path)) {
            throw this.newError("PathSyntaxInvalid", path);
        }
        // check for path existence if configured to do so
        if (this.settings["throwPathDoesNotExistErrors"] && !has(this.data, path)) {
            throw this.newError("PathDoesNotExist", path);
        }
        let value = get(this.data, path, defaultValue);
        if (clone) {
            value = Type.clone(value);
        }
        // return the value
        return value;
    }
    /**
     * Returns the type of the element at the specified path
     * @param path path to the element
     * @returns the type of the element
     */
    typeOf(path) {
        return Type.typeOf(this.get(path));
    }
    set(path, value, mode = 'replace', clone = false) {
        // handle the case where set is called with only one parameter (i.e. the 'value' parameter here is undefined and 'path' is implied to be "", so we are supposed to interpret the first parameter to be the 'value' to set)
        // if (value === undefined) {
        if (arguments.length === 1) {
            value = path; // remember, in this case only one argument was passed named "path" but we interpret it as the "value" to set at the root (where the path is implicitly the empty string "")
            path = '';
        }
        const resolvedPath = path; // it's always a string from here down
        // check for special array syntax: a[] (append) and a[2i] (insert at index 2)
        const pathIsString = typeof resolvedPath === 'string';
        const lastTwoPathChars = (pathIsString) ? resolvedPath.slice(-2) : '';
        if (pathIsString && lastTwoPathChars === '[]') {
            // if the remaining path is to an array, append
            const arrayPath = resolvedPath.slice(0, resolvedPath.length - 2);
            const exists = this.has(arrayPath);
            const isArray = this.typeOf(arrayPath) === 'array';
            // here the array existed, so we can just do the insert (we rely on the arrayInsert function to error if necessary for invalid indexes)
            if (exists) {
                if (isArray) {
                    const array = this.get(arrayPath);
                    array.push(value); // updates the array by reference
                }
                else {
                    throw this.newError("SpecialArrayPathSyntaxTargetedExistingNonArray", resolvedPath);
                }
            }
            // here the array doesn't exist, so we create it with the single element
            else {
                set(this.data, arrayPath, [value]);
            }
        }
        else if (pathIsString && lastTwoPathChars === 'i]') {
            // make sure we have a proper indexing path
            const matches = resolvedPath.match(/(.+)\[(-?\d+)i\]$/);
            if (matches === null) {
                throw this.newError("InvalidSpecialArrayInsertPathSyntax", resolvedPath);
            }
            // note: matches[0] is the whole match
            const arrayPath = matches[1];
            const index = parseInt(matches[2]);
            const exists = this.has(arrayPath);
            const isArray = this.typeOf(arrayPath) === 'array';
            // here the array existed, so we can just do the insert (we rely on the arrayInsert function to error if necessary for invalid indexes)
            if (exists) {
                if (isArray) {
                    const array = this.get(arrayPath);
                    const newArray = arrayInsert(array, index, value);
                    set(this.data, arrayPath, newArray);
                }
                else {
                    throw this.newError("SpecialArrayPathSyntaxTargetedExistingNonArray", resolvedPath);
                }
            }
            else {
                if (index === 0) {
                    set(this.data, arrayPath, [value]); // create the array with the single element at that path
                }
                else {
                    throw this.newError("SpecialArrayPathInsertSyntaxTargetedNonExistingElementWithNonZeroInsertIndex", resolvedPath);
                }
            }
        }
        else {
            // if we have an empty path and the value is not an object, throw an error assign the top level data
            if (this.isEmptyDataPath(resolvedPath) && !Type.isObject(value)) {
                throw this.newError("RootDataMustBeObject", value);
            }
            // otherwise if the path is invalid and we're throwing such errors, then throw one
            else if (typeof resolvedPath === 'string' && this.settings["throwPathSyntaxInvalidErrors"] && !DataPath.isValidPath(resolvedPath)) {
                throw this.newError("PathSyntaxInvalid", resolvedPath);
            }
            // clone the value if specified
            if (clone) {
                value = Type.clone(value);
            }
            switch (mode) {
                case 'replace':
                    // if path is empty it refers to the root object so just set it
                    if (this.isEmptyDataPath(resolvedPath)) {
                        this.data = value; // we know value must be an object in this case, else an error would have been thrown above
                    }
                    else {
                        // remove what's currently there and replace it with the value
                        unset(this.data, resolvedPath);
                        set(this.data, resolvedPath, value);
                    }
                    break;
                case 'append':
                case 'update':
                case 'merge':
                    if (this.isEmptyDataPath(resolvedPath)) {
                        // we know value is of type object here, else an error would have been thrown above
                        objectJoin(this.data, value, mode);
                    }
                    else {
                        const assignment = {};
                        set(assignment, resolvedPath, value);
                        objectJoin(this.data, assignment, mode);
                    }
                    break;
            }
        }
    }
    /**
     * Removes the value at the specified path. Note: we do NOT return a copy of the data here; the caller must obtain their own copy before deleting if desired.  Furthermore, we use the "remove" semantic here insted of "delete" since for arrays we techincally do NOT delete the key, rather it gets set to undefined.
     * @param path the data path to remove
     */
    remove(path) {
        // if path is the empty string, we interpret it to mean clear our data by setting it to an empty object
        if (path === undefined || this.isEmptyDataPath(path)) {
            this.data = {};
            return true;
        }
        // otherwise attempt to remove the element at the specified path
        else {
            // check for path syntax errors if configured to do so
            if (typeof path === 'string' && this.settings["throwPathSyntaxInvalidErrors"] && !DataPath.isValidPath(path)) {
                throw this.newError("PathSyntaxInvalid", path);
            }
            // check for path syntax errors if configured to do so
            if (this.settings["throwPathDoesNotExistErrors"] && !has(this.data, path)) {
                throw this.newError("PathDoesNotExist", path);
            }
            return unset(this.data, path);
        }
    }
    /**
     * Sets a setting
     * @param setting the setting to set
     * @param value the value to set
     */
    setSetting(setting, value) {
        this.settings[setting] = value;
    }
    /**
     * Gets a setting
     * @param setting the setting to get
     * @returns the specified setting's value
     */
    getSetting(setting) {
        return this.settings[setting];
    }
    /**
     * Performs a walk over the underlying data object
     * @param handler the handler function to call on each visited element
     * @param direction either fromRootDownward or fromLeavesUpward indicating whether to call the handler as we walk down or as we traverse back up
     * @param rootPath the path at which to start the walk
     */
    walk(handler, direction = 'fromRootDownward', rootPath = '') {
        const value = this.get(rootPath);
        const strippedPathBits = DataPath.stripPath(rootPath);
        const parentPath = strippedPathBits.basePath;
        const key = strippedPathBits.leaf;
        this.walkRecursive(handler, direction, parentPath, key, value);
    }
    /* Helper Functions */
    /**
     * Recursively walks the data while calling the specified handler
     * @param handler the handler function to call on walked data
     * @param direction the direction to walk
     * @param parentPath the parent path at the current level during the walk
     * @param key the key of the current data element being walked
     * @param value the value of the current data element being walked
     * @returns false if the walk handler returns false, true otherwise
     */
    walkRecursive(handler, direction, parentPath, key, value) {
        // if we have a simple value, call our handler
        if (Type.isPrimitiveIterable(value) === false) {
            const returnValue = this.callWalkHandler(handler, parentPath, key, value);
            return (returnValue !== false);
        }
        // create the parent path to pass to recursive calls for children. note: if parent path is empty, return just the key (which notably will be an empty string for the topmost root object in data manager <-- the handler will be called on the top level object when rootPath = empty string)
        const childParentPath = (parentPath === '') ? key : parentPath + '.' + key;
        // if we get here, we have a so-called "primitive iterable", so walk into it
        switch (direction) {
            // if any node fails stop walking down under that branch
            case 'fromRootDownward':
                const rootDownReturnValue = this.callWalkHandler(handler, parentPath, key, value);
                if (rootDownReturnValue !== false) {
                    for (const childKey in value) {
                        this.walkRecursive(handler, direction, childParentPath, childKey, value[childKey]);
                    }
                }
                return true;
            // if all children fail stop walking up along that branch
            case 'fromLeavesUpward':
                let keepWalkingUp = false; // assume all children return false
                for (const childKey in value) {
                    const childReturnValue = this.walkRecursive(handler, direction, childParentPath, childKey, value[childKey]);
                    // if any child returns ok (i.e. does not return false), we'll keep going!
                    if (childReturnValue !== false) {
                        keepWalkingUp = true;
                    }
                }
                if (keepWalkingUp) {
                    const returnValue = this.callWalkHandler(handler, parentPath, key, value);
                    if (returnValue !== false) {
                        return true;
                    }
                }
                return false;
            // if any child fails stop walking up along that branch
            case 'fromLeavesUpwardStrict':
                let stopWalkingUp = false; // assume all children return ok (i.e. don't return false)
                for (const childKey in value) {
                    const childReturnValue = this.walkRecursive(handler, direction, childParentPath, childKey, value[childKey]);
                    // if any child returns false, we'll stop walking up
                    if (childReturnValue === false) {
                        stopWalkingUp = true;
                    }
                }
                if (stopWalkingUp === false) {
                    const returnValue = this.callWalkHandler(handler, parentPath, key, value);
                    if (returnValue !== false) {
                        return true;
                    }
                }
                return false;
        }
    }
    /**
     * Calls a walk handler
     * @param handler
     * @param parentPath
     * @param key
     * @param value
     */
    callWalkHandler(handler, parentPath, key, value) {
        // simple handler case
        if (typeof handler === 'function') {
            return handler.call(undefined, key, value, parentPath, undefined);
        }
        // handler object case (with added scope and hold data)
        return handler.callback.call(handler.scope, key, value, parentPath, handler.holdData);
    }
    /**
     * Constructs an instance of a specified error
     * @param error the error to construct
     * @param args the arguments to use to construct the error
     * @returns the specified error
     */
    newError(error, ...args) {
        switch (error) {
            case "RootDataMustBeObject":
                return new Error("Error: RootDataMustBeObject. The top level data type stored in Data must be an object.  Instead, the following type was received: " + args[0]);
            case "PathSyntaxInvalid":
                return new Error("Error: PathInvalid. Invalid path syntax '" + args[0] + "' detected.  Note: the setting 'throwPathSyntaxInvalidErrors' is true.");
            case "PathDoesNotExist":
                return new Error("Error: PathDoesNotExist. The path '" + args[0] + " did not exist'. Note: the setting 'throwPathDoesNotExistErrors' is true.");
            case "InvalidQuery":
                return new Error("Error: InvalidQuery. Invalid query statement syntax was detected in the following statement: " + args[0] + ".");
            case "InvalidSpecialArrayInsertPathSyntax":
                return new Error("Error: InvalidSpecialArrayInsertPathSyntax. The following path used invalid array insert syntax: " + args[0] + ".  An example of valid syntax is a.b[2i] which would insert at some array at path 'a.b' at index 2.");
            case "SpecialArrayPathInsertSyntaxTargetedNonExistingElementWithNonZeroInsertIndex":
                return new Error("Error: SpecialArrayPathInsertSyntaxTargetedNonExistingElementWithNonZeroInsertIndex. The following path used the array insert syntax: " + args[0] + " and the path was to a non-existing element, which is only allowed if the index specified is 0 (in which case an array with the single element is created).");
            case "SpecialArrayPathSyntaxTargetedExistingNonArray":
                return new Error("Error: SpecialArrayPathSyntaxTargetedExistingNonArray. The following path used the array insert syntax: " + args[0] + " however the path to the specified array element already existed and was not an array.");
        }
    }
    /**
     * Expands an number-annotated edge to it's full canonical edge notation
     * ex. transforms -3-> into -->(true)-->(true)-->
     * @param query the query to expand edges for
     * @returns the full canonical edge notation
     */
    queryExpandEdges(query) {
        return query.replace(/-(?:(\d+)|\{(\d+)\}|\[(\d+)\])->/g, function (match, anyDepth, objectDepth, arrayDepth, offset, query) {
            // Example: bits such as "-3->" become "-->(true)-->(true)-->"
            // resolve the edge depth (the regex above only matches if a depth WAS provided, so we can safely assume arrayDepth exists if the other two do not)
            let N = (anyDepth !== undefined) ? anyDepth : (objectDepth !== undefined) ? objectDepth : arrayDepth;
            // generate the expansion
            const edge = match.replace(/\d+/, '');
            let expansion = edge;
            N = N - 1; // we initialize expansion to the leading edge, so we only need to append N-1 more
            for (let i = 0; i < N; i++) {
                expansion += '{[(true)]}' + edge;
            }
            return expansion;
        });
    }
    /**
     * Compiles a query statement
     * @param statement the query statement to compile
     * @returns the compiled query statement
     */
    queryCompileStatement(statement) {
        // parse the statement into select, as, and condition bits
        // compile each select into a function which returns the select object; this function will accept elementPath, elementValue, and dataManager arguments (reference to "this" data manager instance)
        // for the AS clause we simply extract the "result name" provided
        // for each condition, walk the string parsing out the next expression:
        // - compile each expression into a function which receives: elementPath, elementValue, and dataManager arguments (reference to "this" data manager instance)
        // - build up an "overall condition" string which implements the overall condition by replacing expressions with expressionTruthsForAtLeastOneKey[n]
        // compile the "overall condition" string into a function which accepts a boolean array argument "expressionTruthsForAtLeastOneKey" which represents whether at least one key of an object met that expression in the condition
        // conditions then compile to: { compiledExpressions: [<function>, ..., <function>], compiledOverallCondition: <function> }
        // each statement compiles to: { compiledSelect:<function>, resultSet: "name", compiledCondition: <compiled condition> }
        const statementMatches = this.statementExecRegex.exec(statement); // we actually validate the entire query in the compileQuery() function so we can only ever reach this point with a valid, parsable statement (at least to the extent that our current regexe's can distinguish), but because we call into this.throwError to throw the error (instead of explicitly throwing it), TS thinks downstream uses might be the theoretical null value which the regex exec() function can return
        const select = statementMatches[1];
        const selectAs = statementMatches[2];
        const limit = statementMatches[3];
        const orderBy = statementMatches[4];
        const sort = statementMatches[5];
        const condition = statementMatches[6];
        // walk the condition string and for each expression: create a function for the expression and replace it with expressionTruthsForAtLeastOneKey[n] (used to build our overall condition function)
        const compiledExpressions = [];
        let expression = '';
        let overallCondition = condition.substr(1, condition.length - 2); // remove the leading and trailing curly braces
        let i = 0;
        let expressionMatches = this.expressionExecRegex.exec(condition);
        while (expressionMatches !== null) {
            // expressionMatches[1] is the regex submatch = just the expression with parentheses
            expression = expressionMatches[1];
            // add our compiled expression (as a function) to our list
            compiledExpressions.push(new Function('$elementInfo', '$childElementInfo', '$dataManager', 'return ' + expression + ';'));
            // replace the expression in our overall condition
            overallCondition = overallCondition.replace(this.expressionReplaceRegex, 'expressionTruthsForAtLeastOneKey[' + i + ']');
            // increment our index
            i = i + 1;
            // find the next expression if it exists
            expressionMatches = this.expressionExecRegex.exec(condition); // the regex here uses the 'g' flag so we're moving forward in the string as we match here
        }
        // now we can put together our compiled condition which includes the compiled expressions and compiled overall condition
        const compiledOverallConditionDefinition = `
            return ( ${overallCondition} );
        `;
        const compiledCondition = { compiledExpressions: compiledExpressions, compiledOverallCondition: new Function('expressionTruthsForAtLeastOneKey', compiledOverallConditionDefinition) };
        // create the compiled SELECT function if we had a SELECT clause
        const compiledSelectDefinition = `
            // since we allow any type to be selected we do not perform any checks here; we simply return the SELECT clause as-is
            return ${select};
        `;
        let compiledSelect = undefined;
        if (select !== undefined) {
            compiledSelect = new Function('$elementInfo', '$dataManager', '$results', compiledSelectDefinition);
        }
        // create the compiled AS function if we had a AS clause
        const compiledSelectAsDefinition = `
            const selectAs = ${selectAs};
            if (typeof selectAs !== "string") {
                throw new Error('Error: Invalid query AS clause. The AS clause must resolve to a string. Instead the AS clause resolved to: ' + selectAs);
            }
            return selectAs;
        `;
        let compiledSelectAs = undefined;
        if (selectAs !== undefined) {
            compiledSelectAs = new Function('$elementInfo', '$dataManager', '$results', compiledSelectAsDefinition);
        }
        // create the compiled LIMIT function if we had a LIMIT clause
        const compiledLimitDefinition = `
            const limit = ${limit};
            if (typeof limit !== "number") {
                throw new Error('Error: Invalid query LIMIT clause. The LIMIT clause must resolve to an integer. Instead the LIMIT clause resolved to: ' + limit);
            }
            return limit;
        `;
        let compiledLimit = undefined;
        if (limit !== undefined) {
            compiledLimit = new Function('$elementInfo', '$dataManager', '$results', compiledLimitDefinition);
        }
        // create the compiled ORDER BY function if we had an ORDER BY clause
        const compiledOrderByDefinition = `
            let orderBy = ${orderBy};

            // if it's a string, make it an array of that single string
            if (typeof orderBy === "string") {
                orderBy = [orderBy];
            }

            // if it's not an array at this point, throw an error (we only allows strings or arrays, and we handled the string case above)
            if (!Array.isArray(orderBy)) {
                throw new Error('Error: Invalid query ORDER BY clause. The ORDER BY clause must resolve to either a string or an array of strings. Instead the ORDER BY clause resolved to: ' + orderBy);
            }

            // if we make it this far, return orderBy
            return orderBy;
        `;
        let compiledOrderBy = undefined;
        if (orderBy !== undefined) {
            compiledOrderBy = new Function('$elementInfo', '$dataManager', '$results', compiledOrderByDefinition); // should return an array of strings such as: ["a", "b"] where a, b are elements of result objects (if results are not objects, use an empty array [])
        }
        // create the compiled ORDER BY "directions" function if we had a ORDER BY "directions" clause
        const compiledSortDefinition = `
            let sort = ${sort};

            // if it's a string, make it an array of that single string
            if (typeof sort === "string") {
                sort = [sort];
            }

            // if it's not an array at this point, throw an error (we only allows strings or arrays, and we handled the string case above)
            if (!Array.isArray(sort)) {
                throw new Error('Error: Invalid query SORT clause. The SORT clause must resolve to either a string or an array of strings (which can be an empty array for sorting scalar results).  Instead the SORT clause resolved to: ' + sort);
            }

            // define a validation function to make sure we have an array of strings containing only values: "ASC", "DESC", "ASCENDING", and "DESCENDING"
            const validate = function(value) {
                if (typeof value !== "string") {
                    return false;
                }
                const validValues = ["ASC", "DESC", "ASCENDING", "DESCENDING"];
                if (!validValues.includes(value)) {
                    return false;
                }
                return true;
            }

            // if the array contains anything that doesn't equal one of "ASC", "DESC", "ASCENDING", and "DESCENDING" then throw an error
            if (!sort.every(validate)) {
                throw new Error('Error: Invalid query SORT clause. The SORT clause must resolve to either a string or an array of strings (which can be empty implying the use of "ASC") matching one of "ASC", "DESC", "ASCENDING", and "DESCENDING".  Instead the SORT clause resolved to: ' + sort);
            }

            // if we make it this far, return sort
            return sort;
        `;
        let compiledSort = undefined;
        if (sort !== undefined) {
            compiledSort = new Function('$elementInfo', '$dataManager', '$results', compiledSortDefinition); // this should return an array of strings such as: ["ASC", "DESC"]
        }
        return {
            compiledSelect: compiledSelect,
            compiledSelectAs: compiledSelectAs,
            compiledLimit: compiledLimit,
            compiledOrderBy: compiledOrderBy,
            compiledSort: compiledSort,
            compiledCondition: compiledCondition
        };
    }
    /**
     * Executes a compiled query's body and returns true if all condition "IF" clauses indeed matched, else returns false
     * @param compiledQuery the compiled query to execute
     * @param element the current data element (ex. reference to an object when processing at that level of the data)
     * @param path the current path in the underlying data object where the query is processing
     * @param results the current results object
     * @param stage the current stage in the query hierarchy
     */
    queryExecuteBody(compiledQuery, element, path, results, stage = 0) {
        // JQL Algorithm
        // -------------
        // - recursively walk starting from the base element object
        // CONDITION CONCEPT:
        // - at each object level, iterate the keys and build a 2D array = an array of the arrays described below:
        // -- for each key, build an array of booleans by evaluating the expressions like `[<expression>]` in the corresponding query statement's condition
        // - by ORing down the columns of the 2D array we get an array of booleans where indexes corresponds to "expressions in the condition" and values corresponds to "whether any element in the object met that expression"
        // CONDITION IMPLEMENTATION: in practice we needn't keep a 2D array... as we process the keys, we can perform the ORing logic as we go, and even check whether the condition has been met after processing each key (as soon as it has, we can proceed to lower levels)
        // - then by evaluating the logical combination defined in the condition to the elements of the resulting array we get "whether the keys at the object level collectively meet the condition"
        // - if the condition was met (true), recurse down into keys unless we're at the query's tail (when we come out of this recursion, assuming the downstream conditions all passed, we'll gather up test results accordingly)
        // - if the condition was not met (false), stop processing the query
        // - return true if the condition is met, false otherwise
        // choose the next compiled statement and compiled edge
        const compiledStatement = compiledQuery.compiledStatements[stage];
        const compiledEdge = compiledQuery.compiledEdges[stage];
        // @ts-ignore: we checked for the empty case (for which shift() would return undefined) above. now, for each key of element: evaulate each expression in the condition and build a corresponding array of booleans
        const overallConditionMet = this.queryEvaluateCondition(compiledStatement.compiledCondition, element, path);
        // if the condition was not met, return false as the query has no matches
        if (overallConditionMet === false) {
            return false;
        }
        /*************************************************************
         BEYOND THIS POINT: overallConditionMet for this level is true
         *************************************************************/
        let downstreamConditionMet = false;
        // now, if compiledEdge === undefined then there is no more downstream conditions so we set downstreamConditionMet to true so we can still collect results below
        if (compiledEdge === undefined) {
            downstreamConditionMet = true;
        }
        else {
            // recurse down into children to continue evaluating the query; at least one child (think "downstream path") must meet it's condition in order for the query to be satisfied overall
            switch (Type.typeOf(element)) {
                case 'object':
                    // iterate the object and make recursive calls
                    const keys = Object.keys(element);
                    const K = keys.length;
                    for (let i = 0; i < K; i++) {
                        const key = keys[i];
                        const childElement = element[key];
                        const childElementType = Type.typeOf(childElement);
                        // recursively walk into the child if its type matches its incoming edge type
                        if (compiledEdge === 0 || (childElementType === 'object' && compiledEdge === 1) || (childElementType === 'array' && compiledEdge === 2)) {
                            // if any downstream elements meet the condition then we have at least one success so we OR as we walk
                            const childPath = (path === '') ? key : path + '.' + key;
                            const childConditionMet = this.queryExecuteBody(compiledQuery, childElement, childPath, results, stage + 1);
                            downstreamConditionMet = downstreamConditionMet || childConditionMet;
                        }
                    }
                    break;
                case 'array':
                    // iterate the array and make recursive calls
                    const N = element.length;
                    for (let i = 0; i < N; i++) {
                        const childElement = element[i];
                        const childElementType = Type.typeOf(childElement);
                        // recursively walk into the child if its type matches its incoming edge type
                        if (compiledEdge === 0 || (childElementType === 'object' && compiledEdge === 1) || (childElementType === 'array' && compiledEdge === 2)) {
                            // if any downstream elements meet the condition then we have at least one success so we OR as we walk
                            const childPath = path + '[' + i + ']';
                            const childConditionMet = this.queryExecuteBody(compiledQuery, childElement, childPath, results, stage + 1);
                            downstreamConditionMet = downstreamConditionMet || childConditionMet;
                        }
                    }
                    break;
            }
        }
        // if the downstream query matched AND we have a SELECT-AS clause then gather up the result
        const compiledSelect = compiledStatement.compiledSelect;
        const compiledSelectAs = compiledStatement.compiledSelectAs;
        if (downstreamConditionMet && compiledSelect !== undefined && compiledSelectAs !== undefined) { // note: techincally we don't NEED to check both compiledSelet and selectAs here since they'll either both be undefined or not based on how we regex the select clause, but doing so keeps TS happy since it can statically guarantee that selectAs is not undefined for use within the IF statement
            const elementInfo = { key: DataPath.stripPath(path).leaf, path: path, value: element };
            const selectAsKey = compiledSelectAs(elementInfo, this, results); // returns a string
            if (!results.hasOwnProperty(selectAsKey)) {
                results[selectAsKey] = [];
            }
            // cache a reference to this set of results so we don't have to re-index the object a bunch of times below
            const currentResults = results[selectAsKey];
            // if we have a defined LIMIT clause, enforce it
            const compiledLimit = compiledStatement.compiledLimit;
            if (compiledLimit !== undefined) {
                const resolvedLimit = compiledLimit(elementInfo, this, results);
                if (results[selectAsKey].length < resolvedLimit || resolvedLimit < 0) {
                    results[selectAsKey].push(compiledSelect(elementInfo, this, results));
                }
            }
            // otherwise simply append the new result unconditionally
            else {
                results[selectAsKey].push(compiledSelect(elementInfo, this, results));
            }
            // if we have an ORDER BY clause, enforce it (otherwise just leave the current results alone)
            const compiledOrderBy = compiledStatement.compiledOrderBy;
            if (compiledOrderBy !== undefined) {
                const resolvedOrderByArray = compiledOrderBy(elementInfo, this, results);
                // if we have a SORT clause, enforce it
                let resolvedSortArray = []; // default to ASC if we didn't receive a SORT clause; note: [] simply means sort ASC (and even works for scalar results)
                const compiledSort = compiledStatement.compiledSort;
                if (compiledSort !== undefined) {
                    resolvedSortArray = compiledSort(elementInfo, this, results);
                    // lower case all of the elements to comply with the imported lodash orderBy function used below
                    const N = resolvedSortArray.length;
                    for (let i = 0; i < N; i++) {
                        resolvedSortArray[i] = resolvedSortArray[i].toLowerCase();
                    }
                }
                // sort the results[selectAsKey] to account for the newly "pushed" element above; note that if resolvedOrderByArray is empty or undefined it'll happily sort a scalar array, and if resolvedSortArray is empty or undefined the function below defaults to ASC sort order
                results[selectAsKey] = orderBy(results[selectAsKey], resolvedOrderByArray, resolvedSortArray);
            }
        }
        // recall: the only way reach this point in code is if overallConditionMet is true so we needn't "AND" with overallConditionMet here
        return downstreamConditionMet;
    }
    /**
     * Evaluates a compiled condition
     * @param compiledCondition the condition to evaluate
     * @param element the element at which to evaluate the condition
     * @param path the path in data where the condition is being evaluated
     */
    queryEvaluateCondition(compiledCondition, element, path) {
        const compiledExpressions = compiledCondition.compiledExpressions;
        const numberOfExpressions = compiledExpressions.length;
        let numberOfChildElements = 0;
        // initialize the array which tracks the "column ORing" logic
        const expressionTruthsForAtLeastOneKey = Array(numberOfExpressions).fill(false);
        // iterate objects and arrays
        switch (Type.typeOf(element)) {
            case 'object':
                // iterate the elements of the object
                const keys = Object.keys(element);
                numberOfChildElements = keys.length;
                for (let i = 0; i < numberOfChildElements; i++) {
                    // for the current element: iterate the expressions and keep track of whether each has been met (is true)
                    for (let j = 0; j < numberOfExpressions; j++) {
                        if (expressionTruthsForAtLeastOneKey[j] === false) {
                            const key = keys[i];
                            const elementInfo = { key: DataPath.stripPath(path).leaf, path: path, value: element };
                            const childElementInfo = { key: key, path: path + '.' + key, value: element[key] };
                            expressionTruthsForAtLeastOneKey[j] = compiledExpressions[j](elementInfo, childElementInfo, this);
                        }
                    }
                    if (arrayAND(expressionTruthsForAtLeastOneKey) === true) {
                        break; // if every expression has already been true for at least one key, there's nothing else to resolve
                    }
                }
                break;
            case 'array':
                // iterate the elements of the array
                numberOfChildElements = element.length;
                for (let i = 0; i < numberOfChildElements; i++) {
                    // for the current element: iterate the expressions and keep track of whether each has been met (is true)
                    for (let j = 0; j < numberOfExpressions; j++) {
                        if (expressionTruthsForAtLeastOneKey[j] === false) {
                            const elementInfo = { key: DataPath.stripPath(path).leaf, path: path, value: element };
                            const childElementInfo = { key: i, path: path + '[' + i + ']', value: element[i] };
                            expressionTruthsForAtLeastOneKey[j] = compiledExpressions[j](elementInfo, childElementInfo, this);
                        }
                    }
                    if (arrayAND(expressionTruthsForAtLeastOneKey) === true) {
                        break; // if every expression has already been true for at least one key, there's nothing else to resolve
                    }
                }
                break;
            default:
                // we have no keys to iterate since we're at a scalar element. just evaluate all the expressions against element with childElementInfo = undefined
                for (let j = 0; j < numberOfExpressions; j++) {
                    const elementInfo = { key: DataPath.stripPath(path).leaf, path: path, value: element };
                    const childElementInfo = undefined;
                    expressionTruthsForAtLeastOneKey[j] = compiledExpressions[j](elementInfo, childElementInfo, this);
                }
                break;
        }
        // finally, evaluate and return the value for the compiledOverallCondition
        return compiledCondition.compiledOverallCondition(expressionTruthsForAtLeastOneKey);
    }
}
//# sourceMappingURL=Data.js.map